import { useMemo } from 'react';

import { useLocation } from 'react-router-dom';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface CurrentCustomerIdResult {
  customerId: string | null;
  orderId: string | null;
}

function extractUuidFromSegments(segments: Array<string>, afterSegment: string): string | null {
  const idx = segments.indexOf(afterSegment);
  if (idx >= 0 && idx + 1 < segments.length) {
    const candidate = segments[idx + 1];
    if (UUID_RE.test(candidate)) return candidate;
  }
  return null;
}

/**
 * Inspects the current URL to determine which customer (or order) the admin is
 * currently looking at. Returns a customer UUID when one is directly available,
 * or an order UUID when on an order page (so the caller can resolve the customer).
 */
export function useCurrentCustomerId(): CurrentCustomerIdResult {
  const { pathname, search } = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(search);

    // 1. Query params take priority
    const customerIdParam = params.get('customer_id');
    if (customerIdParam && UUID_RE.test(customerIdParam)) {
      return { customerId: customerIdParam, orderId: null };
    }

    const userIdParam = params.get('user_id');
    if (userIdParam && UUID_RE.test(userIdParam)) {
      return { customerId: userIdParam, orderId: null };
    }

    // 2. Path-based detection
    const segments = pathname.split('/').filter(Boolean);

    // /users/<uuid> or /users/<uuid>/...
    const userUuid = extractUuidFromSegments(segments, 'users');
    if (userUuid) return { customerId: userUuid, orderId: null };

    // /purchases/<uuid>, /edit-purchases/<uuid>, /bookings/<uuid> -- these are order IDs
    for (const segment of ['purchases', 'edit-purchases', 'bookings']) {
      const orderUuid = extractUuidFromSegments(segments, segment);
      if (orderUuid) return { customerId: null, orderId: orderUuid };
    }

    return { customerId: null, orderId: null };
  }, [pathname, search]);
}
